import React, { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Avatar from 'components/base/Avatar';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen, faTrash, faKey } from '@fortawesome/free-solid-svg-icons';
import { deleteAccount } from 'services/account.service';
import AuthService from 'services/auth.service';
import UpdateFullUserForm from 'components/forms/UpdateFullUserForm';
import UpdateSAMLUserForm from 'components/forms/UpdateSAMLUserForm';
import tfaicon from 'assets/img/icons/tfa.png';
import Badge from 'components/base/Badge';

type Account = {
  fullname: string;
  id: number;
  activation_code: boolean;
  username: string;
  email: string;
  registerat: string;
  last_seen: string;
  roles: string;
  avatar?: string;
  auth_type?: 'local' | 'saml-azure' | 'saml-manageengine';
};

const AccountActionCell = ({
  id,
  auth_type,
  onUpdate
}: {
  id: number;
  auth_type?: string;
  onUpdate?: () => Promise<void>;
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = (id: number) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleCloseResetPasswordModal = () => setShowResetPasswordModal(false);
  const handleShowResetPasswordModal = (id: number) => {
    setSelectedId(id);
    setShowResetPasswordModal(true);
  };

  const handleCloseEditModal = async () => {
    setShowEditModal(false);
    if (onUpdate) {
      await onUpdate();
    }
  };
  const handleShowEditModal = (id: number) => {
    setSelectedId(id);
    setShowEditModal(true);
  };

  const handleConfirmResetPassword = async () => {
    if (selectedId !== null) {
      try {
        await AuthService.resetPassword(selectedId, newPassword);
      } catch (error) {
        console.error(`Error resetting password on ID: ${selectedId}`, error);
      } finally {
        handleCloseResetPasswordModal();
      }
    }
  };

  const handleConfirmRemove = async () => {
    if (selectedId !== null) {
      try {
        await deleteAccount(selectedId);
        if (onUpdate) {
          await onUpdate();
        }
        setShowDeleteModal(false);
      } catch (error) {
        console.error(`Error deleting account with ID: ${selectedId}`, error);
      }
    }
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={() => handleShowEditModal(id)}>
          <FontAwesomeIcon icon={faUserPen} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        {auth_type == 'local' && (
          <>
            <Dropdown.Item onClick={() => handleShowResetPasswordModal(id)}>
              <FontAwesomeIcon icon={faKey} className="fs-8 p-1 pt-0 pb-0" />
              Password reset
            </Dropdown.Item>
          </>
        )}
        <Dropdown.Item
          onClick={() => handleShowDeleteModal(id)}
          className="text-primary-dark"
        >
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this account?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showResetPasswordModal}
        onHide={handleCloseResetPasswordModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset User Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formNewPassword">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseResetPasswordModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmResetPassword}>
            Reset Password
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Account</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {auth_type?.startsWith('saml') ? (
            <UpdateSAMLUserForm
              accountId={selectedId!}
              onClose={handleCloseEditModal}
              onUpdate={onUpdate}
            />
          ) : (
            <UpdateFullUserForm
              accountId={selectedId!}
              onClose={handleCloseEditModal}
              onUpdate={onUpdate}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export const accountsTablecolumns = (
  refreshData?: () => Promise<void>
): ColumnDef<Account>[] => [
  {
    accessorKey: 'fullname',
    header: 'Full name',
    cell: ({ row: { original } }) => {
      const { fullname, avatar } = original;
      return (
        <div className="d-flex align-items-center text-body-emphasis">
          <Avatar src={avatar} size="m" />
          <p className="mb-0 ms-2 fw-bold">{fullname}</p>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '20%' }, className: 'pe-2 ps-0' },
      cellProps: { className: 'py-0 pe-2 ps-0 p-0 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'auth_type',
    header: 'Auth',
    cell: ({ row: { original } }) => {
      const { auth_type } = original;
      let badgeText = 'Local';

      switch (auth_type) {
        case 'saml-azure':
          badgeText = 'Azure';
          break;
        case 'saml-manageengine':
          badgeText = 'ADS';
          break;
      }

      return (
        <Badge
          variant="phoenix"
          bg={auth_type === 'local' ? 'warning' : 'secondary'}
        >
          {badgeText}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'text-center px-1' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      const { id, auth_type } = original;
      return (
        <AccountActionCell
          id={id}
          auth_type={auth_type}
          onUpdate={refreshData}
        />
      );
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'fw-semibold text-body-highlight px-1' }
    }
  },
  {
    accessorKey: 'activation_code',
    header: 'TFA',
    cell: ({ row: { original } }) => {
      const { activation_code } = original;
      if (activation_code) {
        return (
          <>
            <img className="P-0" src={tfaicon} alt="TFA" />
          </>
        );
      }
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'username',
    header: 'Username',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row: { original } }) => {
      const { email } = original;
      return (
        <Link to={`mailto:${email}`} className="fw-semibold text-primary-dark">
          {email}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '20%' }, className: 'pe-5 ps-1' },
      cellProps: { className: 'pe-5 ps-1' }
    }
  },
  {
    accessorKey: 'registerat',
    header: 'Registered',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'last_seen',
    header: 'Last seen',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'roles',
    header: 'Authorization',
    meta: {
      headerProps: { style: { width: '25%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  }
];

export const AccountsTable = () => {
  return (
    <div>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
